import React, { useEffect, useMemo, useState } from 'react';
import { useAppActions, useAppState } from '../../../overmind';
import { Button, Chip, DropdownSelect, DropdownSelectOption, Filters, formatDate, PageHeader, Pager, SearchBar, SkillLevel, SkillPercentage } from '@keplerco/core';
import { PagePath } from '../../../navigation/navigation.enums';
import { CompletionStatus } from '../../../enums';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import styles from './assessments.module.css';
import classNames from 'classnames';
import { SkeletonLoader } from '../../../components/general/loading-state/loaders/skeleton-loader/skeleton-loader';
import { useKeplerNavigate } from '../../../navigation/guards/use-kepler-navigate';
import { PagerConnector } from '../../../components/general/pager-connector/pager-connector';
import { AssessmentStatusChip } from '../../../components/chips/assessment-status.chip';
import { ASSESSMENT_SUB_TYPE_OPTIONS, assessmentSortByOptions } from '../../../library/consts/consts';
import { AssessmentListSearchParams, AssessmentListItemResponse } from '../../../overmind/actions';
import { SortByOption } from '../../../enums/sort-by-option';
import { AssessmentStatus } from '../../../enums/assessment-status';
import { getAssessmentSubTypeDescription } from '../../../library/helpers/get-assessment-sub-type-description';
import { FiltersOutlet } from '../analytics-filters/filters-outlet';
import { SelectedFilterOption } from '../analytics-filters/analytics-filters.models';

const pageSize = 10;

export const assessmentStatusOptions: DropdownSelectOption[] = [
  { label: 'Closed', value: CompletionStatus.Completed.toString() },
  { label: 'Active', value: CompletionStatus.InProgress.toString() },
];

enum FilterType {
  AssessmentSubType = 'assessmentSubType',
  AssessmentStatuses = 'assessmentStatuses',
  Tag = 'tag',
}

export default function AssessmentsPage() {
  const actions = useAppActions();
  const { companyVariables } = useAppState();
  const keplerNavigate = useKeplerNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const [tagDropdownOptions, setTagDropdownOptions] = useState<DropdownSelectOption[]>([]);

  const [data, setData] = useState<AssessmentListItemResponse[]>();
  const [totalPages, setTotalPages] = useState<number>(1);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilterOption[]>([]);

  const defaultSearchParams: AssessmentListSearchParams = useMemo(
    () => ({
      companySlug: companyVariables.slug!,
      search: undefined,
      sortBy: SortByOption.MostRecentlyCreated,
      pageSize: pageSize,
      page: 1,
      assessmentStatuses: [AssessmentStatus.Active, AssessmentStatus.Closed, AssessmentStatus.Scheduled],
    }),
    [companyVariables.slug]
  );

  const [searchParams, setSearchParams] = useState<AssessmentListSearchParams>(defaultSearchParams);

  function getSelectedFilters(): SelectedFilterOption[] {
    const filtersSelected = [];

    if (searchParams.assessmentSubType !== undefined) {
      filtersSelected.push({
        name: ASSESSMENT_SUB_TYPE_OPTIONS.find(option => option.value === searchParams.assessmentSubType?.toString())?.label || 'Assessment type',
        filterType: FilterType.AssessmentSubType,
        slug: `${FilterType.AssessmentSubType}${searchParams.assessmentSubType}`,
      });
    }

    const defaultStatuses = [AssessmentStatus.Active, AssessmentStatus.Closed, AssessmentStatus.Scheduled];
    const currentStatuses = searchParams.assessmentStatuses || [];
    const statusesChanged = JSON.stringify(currentStatuses.sort()) !== JSON.stringify(defaultStatuses.sort());

    if (statusesChanged && currentStatuses.length > 0) {
      currentStatuses.forEach(status => {
        filtersSelected.push({
          name: assessmentStatusOptions.find(option => option.value === status.toString())?.label || 'Status',
          filterType: FilterType.AssessmentStatuses,
          slug: `${FilterType.AssessmentStatuses}${status}`
        });
      });
    }

    if (searchParams.tag !== undefined && searchParams.tag !== '') {
      filtersSelected.push({
        name: searchParams.tag,
        filterType: FilterType.Tag,
        slug: `${FilterType.Tag}${searchParams.tag}`,
      });
    }

    return filtersSelected;
  }

  async function getData(searchParams: AssessmentListSearchParams) {
    const response = await actions.getAssessmentList(searchParams);
    setData(response?.assessments);
    setTotalPages(response?.totalPages ?? 1);
    setIsLoading(false);
    setIsSearching(false);
    return response;
  }

  useEffect(() => {
    async function initPage() {
      setIsLoading(true);

      await getData(searchParams);

      const tags = await actions.searchTags({
        companySlug: companyVariables.slug!,
        page: 1,
        pageSize: 99999,
        search: '',
      });

      if (tags) {
        setTagDropdownOptions(tags?.tags.map(tag => ({ label: tag.tagName, value: tag.tagName })));
      }

      setIsInitialized(true);
    }

    initPage();
  }, []);

  async function onInputHandler(event: React.FormEvent<HTMLInputElement>) {
    const inputValue = (event.target as HTMLInputElement).value;
    setIsSearching(true);

    const newRequest = {
      ...searchParams,
      search: inputValue.trim() || undefined,
      page: 1,
    };

    setSearchParams(newRequest);
    await getData(newRequest);
  }

  async function clearFilters() {
    setIsLoading(true);
    const newRequest = { ...defaultSearchParams };
    setSearchParams(newRequest);
    setSelectedFilters([]);
    await getData(newRequest);
  }

  async function applyFilters() {
    setIsLoading(true);
    const newRequest = { ...searchParams, page: 1 };
    setSearchParams(newRequest);
    setSelectedFilters(getSelectedFilters());
    await getData(newRequest);
  }

  async function onPageChangeHandler(pageNumber: number) {
    setIsLoading(true);
    const newRequest = { ...searchParams, page: pageNumber };
    setSearchParams(newRequest);
    await getData(newRequest);
  }

  function generateDates(assessment: AssessmentListItemResponse) {
    const dates = [];

    if (assessment.dateCompleted && assessment.assessmentStatus !== AssessmentStatus.Active) {
      dates.push(`Closed: ${formatDate(new Date(assessment.dateCompleted))}`);
    }
    if (assessment.dateActivated) {
      dates.push(`Activated: ${formatDate(new Date(assessment.dateActivated))}`);
    }

    return dates.length > 0 ? dates : null;
  }

  function renderDates(assessment: AssessmentListItemResponse) {
    const dates = generateDates(assessment);
    if (!dates) return null;

    return (
      <div className={styles.datesContainer}>
        {dates.map(date => (
          <div className={classNames('caption', styles.dateText)} key={date}>
            {date}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="pageWrapper wrapper">
      <PageHeader
        breadcrumbs={[
          { name: 'Analytics', url: PagePath.analyticsBase },
          { name: 'Assessments', url: window.location.pathname },
        ]}
        title="Assessments"
      />
      <div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 16 }}>
          <SearchBar label="Search" loading={isSearching} onInput={onInputHandler} />

          <Filters
            isActive={!!(
              searchParams.assessmentSubType ||
              (searchParams.tag && searchParams.tag !== '') ||
              (searchParams.assessmentStatuses &&
                JSON.stringify((searchParams.assessmentStatuses || []).sort()) !==
                JSON.stringify([AssessmentStatus.Active, AssessmentStatus.Closed, AssessmentStatus.Scheduled].sort()))
            )}
            onClickClear={clearFilters}
            onClickApply={applyFilters}
          >
            <DropdownSelect label="Type" value={searchParams.assessmentSubType?.toString()} options={ASSESSMENT_SUB_TYPE_OPTIONS} onChange={event => setSearchParams(prev => ({ ...prev, assessmentSubType: event.target.value ? parseInt(event.target.value) : undefined }))} />
            <DropdownSelect label="Status" value={searchParams.assessmentStatuses?.toString()} options={assessmentStatusOptions} onChange={event => setSearchParams(prev => ({ ...prev, assessmentStatuses: event.target.value ? [parseInt(event.target.value)] : undefined }))} />
            <DropdownSelect label="Tag" value={searchParams.tag} options={tagDropdownOptions} onChange={event => setSearchParams(prev => ({ ...prev, tag: event.target.value }))} />
          </Filters>

          <DropdownSelect label="Sort by" value={searchParams.sortBy} options={assessmentSortByOptions.filter(option => option.value !== SortByOption.MostRecentlyCreated)} onChange={async event => {
            setIsLoading(true);
            const newSearchParams = { ...searchParams, page: 1, sortBy: event.target.value as SortByOption };
            setSearchParams(newSearchParams);
            await getData(newSearchParams);
            setIsLoading(false);
          }} />
        </div>

        <FiltersOutlet
          selectedFilters={selectedFilters}
          removeFilter={async filter => {
            setIsLoading(true);
            const newSelectedFilters = selectedFilters.filter(f => f.slug !== filter.slug);
            setSelectedFilters(newSelectedFilters);

            const newSearchParams = { ...searchParams, page: 1, [filter.filterType]: undefined };
            setSearchParams(newSearchParams);
            await getData(newSearchParams);
            setIsLoading(false);
          }}
        />
      </div>

      {!isLoading && isInitialized ? (
        <>
          {!!data?.length ? (
            <div className={styles.pageContent} style={{ gap: '12px' }}>
              {data?.map(assessment => {
                const borderLeftColor = assessment.assessmentStatus === AssessmentStatus.Active ? 'a_1' : assessment.assessmentStatus === AssessmentStatus.Closed ? 'g_1' : assessment.assessmentStatus === AssessmentStatus.Archived ? 'default_1' : 'borders';
                return (
                  <div key={assessment.assessmentId} className={classNames('card', styles.card)} style={{ borderLeftColor: `var(--${borderLeftColor})` }}>
                    <div>
                      <div className={styles.headerContainer}>
                        <div className={styles.assessmentTypeDesktop}>{getAssessmentSubTypeDescription(assessment.assessmentSubType)}</div>
                        <div className={styles.statusContainer}>
                          <div className={styles.datesContainer}>{renderDates(assessment)}</div>
                          <AssessmentStatusChip assessmentStatus={assessment.assessmentStatus} />
                        </div>
                      </div>
                      <div className={styles.mobileTypeContainer}>
                        <div className={classNames('caption', styles.assessmentTypeMobile)}>{getAssessmentSubTypeDescription(assessment.assessmentSubType)}</div>
                      </div>
                      <div>
                        <h4 className={styles.assessmentName}>{assessment.name}</h4>
                      </div>
                      <div className={styles.assessmentDetailsContainer}>
                        <div className={styles.assessmentMetricsContainer}>
                          <div className={styles.skillContainer}>
                            {companyVariables.useLevels ? (
                              <SkillLevel title="Assessment score" level={assessment.average?.level ?? null} minLevel={companyVariables.minLevel} maxLevel={companyVariables.maxLevel} noLevel={!assessment.average} />
                            ) : (
                              <SkillPercentage title="Assessment score" percentage={assessment.average?.percentage ?? null} noPercentage={!assessment.average} />
                            )}
                          </div>

                          {assessment.tags.length > 0 && (
                            <div className={styles.tagsContainer}>
                              <div className={classNames('caption', styles.tagsCaption)}>Tags</div>
                              <div className={styles.tagsChipsContainer}>
                                {assessment.tags.map(tag => (
                                  <Chip key={tag} customTheme={{ backgroundColor: 'background', borderColor: 'baby-blue', color: 'text' }}>
                                    {tag}
                                  </Chip>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className={styles.viewButtonContainer}>
                          <Button arrow={false} type={'button'} onClick={() => keplerNavigate(`${PagePath.analyticsBase}${PagePath.analyticsAssessment.replace(':companySlug', companyVariables.slug!).replace(':assessmentSlug', assessment.slug)}`)}>
                            View
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState />
          )}
        </>
      ) : (
        <div style={{ marginTop: 24 }}>
          <SkeletonLoader height="100vh" />
        </div>
      )}

      {totalPages > 1 && (
        <div className={styles.pagerContainer} style={{ marginTop: 24 }}>
          <PagerConnector pageCount={totalPages} onPageChange={onPageChangeHandler} defaultPageNumber={searchParams.page ?? 1}>
            {connector => <Pager {...connector} />}
          </PagerConnector>
        </div>
      )}
    </div>
  );
}
